import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { State } from './../store-types';
import { AppState, IApplicationState, Network, RawNetwork } from './types';
import { camel } from '../../utils/camel-case';
import { fetchCurrentUserStart } from '../../components/account/state/actions';
import { CurrentRoute } from '../../../types';

const DEFAULT_NETWORK_ID = 'spotim';

const initialState: IApplicationState = {
  spotId: '',
  network: {},
  token: '',
  accessToken: '',
  is4O4Error: false,
  isRetryAuth: false
};

const applicationSlice = createSlice({
  name: 'application',
  initialState,
  reducers: {
    initializeAppStart: (state, action: PayloadAction<AppState & { network: RawNetwork }>) => {
      const { spotId, token, accessToken, network } = action.payload;

      state.spotId = spotId;
      state.token = token;
      state.accessToken = accessToken;
      state.network = camel<Network>(network);
    },
    updateAppState: (state, action: PayloadAction<Partial<AppState>>) => {
      setAppState(state, action.payload);
    },
    set4O4Error: (state, action: PayloadAction<boolean>) => {
      state.is4O4Error = action.payload;
    },
    setRetryAuth: (state, action: PayloadAction<boolean>) => {
      state.isRetryAuth = action.payload;
    },
    setCurrentRoute: (state, action: PayloadAction<CurrentRoute>) => {
      state.currentRoute = action.payload;
    },
    updateAppSpotId: (state, action: PayloadAction<string>) => {
      state.spotId = action.payload;
    },
    updateSessionId: (state, action: PayloadAction<string>) => {
      state.sessionId = action.payload;
    }
  },
  extraReducers: builder => {
    builder.addCase(
      fetchCurrentUserStart,
      (state, action: PayloadAction<{ spotId?: string; networkId?: string }>) => {
        setAppState(state, action.payload);
      }
    );
  }
});

export const applicationReducer = applicationSlice.reducer;
export const {
  initializeAppStart,
  updateAppState,
  set4O4Error,
  setRetryAuth,
  setCurrentRoute,
  updateAppSpotId,
  updateSessionId
} = applicationSlice.actions;

function setAppState(
  state: AppState,
  { spotId, network, token, accessToken }: Partial<AppState>
) {
  state.spotId = spotId ?? state.spotId;
  state.network = network
    ? {
        ...state.network,
        ...camel<Partial<Network>>(network)
      }
    : state.network;
  state.token = token ?? state.token;
  state.accessToken = accessToken ?? state.accessToken;
}

// ---------- State Selectors ---------- //

export function getIsDefaultNetwork(state: State) {
  return state?.application?.network?.networkId === DEFAULT_NETWORK_ID;
}
